// src/components/Contact.js
import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import '../styles/Contact.css';
import { sendContactEmail } from '../services/emailService';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });


  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.email || !formData.service || !formData.message) {
      setFormStatus({
        loading: false,
        submitted: false,
        error: true,
        message: 'Please fill in all required fields.'
      });
      return;
    }

    // Set loading state
    setFormStatus({
      loading: true,
      submitted: false,
      error: false,
      message: ''
    });

    try {
      // Send email via MailerSend
      const result = await sendContactEmail(formData);

      if (result.success) {
        // Show success message
        setFormStatus({
          loading: false,
          submitted: true,
          error: false,
          message: result.message
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });

        // Clear success message after 8 seconds
        setTimeout(() => {
          setFormStatus({
            loading: false,
            submitted: false,
            error: false,
            message: ''
          });
        }, 8000);
      }

    } catch (error) {
      console.error('Error submitting form:', error);

      // Show error message
      setFormStatus({
        loading: false,
        submitted: false,
        error: true,
        message: error.message || 'Failed to send message. Please try again or contact us directly at info@edsytech.com'
      });

      // Clear error message after 8 seconds
      setTimeout(() => {
        setFormStatus({
          loading: false,
          submitted: false,
          error: false,
          message: ''
        });
      }, 8000);
    }
  };

  const contactInfo = [
    {
      icon: <Mail className="contact-info-icon" />,
      title: "Email Us",
      details: ["info@edsytech.com"],
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Phone className="contact-info-icon" />,
      title: "Call Us",
      details: ["+91 96423 00006"],
      color: "from-green-500 to-teal-500"
    },
    {
      icon: <MapPin className="contact-info-icon" />,
      title: "Visit Us",
      details: ["Ludhiana, PB 141014, IN"],
      color: "from-purple-500 to-indigo-500"
    },
    {
      icon: <Clock className="contact-info-icon" />,
      title: "Business Hours",
      details: ["Mon - Fri: 9:30 AM - 6:30 PM"],
      color: "from-orange-500 to-red-500"
    }
  ];

  const services = [
    "Frontend Development (Angular/React/Vue)",
    "Backend Development (.NET/C#)",
    "Database Management",
    "Cloud Solutions (Azure/AWS)",
    "Server Configuration",
    "Maintenance & Support",
    "Full Stack Development",
    "Other"
  ];

  return (
    <section id="contact" className="contact-section">
      <div className="contact-container">
        {/* Header */}
        <div className="contact-header">
          <span className="contact-badge">Get In Touch</span>
          <h2 className="contact-title">
            Let's Build Something Amazing Together
          </h2>
          <p className="contact-description">
            Have a project in mind? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        {/* Contact Info Cards */}
        <div className="contact-info-grid">
          {contactInfo.map((info, index) => (
            <div key={index} className="contact-info-card hover-lift">
              <div className={`contact-info-icon-wrapper bg-gradient-to-br ${info.color}`}>
                {info.icon}
              </div>
              <h3 className="contact-info-title">{info.title}</h3>
              <div className="contact-info-details">
                {info.details.map((detail, idx) => (
                  <p key={idx}>{detail}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Main Contact Content */}
        <div className="contact-content-grid">
          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <div className="contact-form-card">
              <h3 className="form-title">Send Us a Message</h3>
              <p className="form-subtitle">Fill out the form below and we'll get back to you shortly</p>

              {formStatus.submitted && (
                <div className="form-alert form-alert-success">
                  <CheckCircle className="alert-icon" />
                  <p>{formStatus.message}</p>
                </div>
              )}

              {formStatus.error && (
                <div className="form-alert form-alert-error">
                  <AlertCircle className="alert-icon" />
                  <p>{formStatus.message}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="company" className="form-label">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="service" className="form-label">
                    Service Interested In <span className="required">*</span>
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select a service</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Your Message <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="Tell us about your project..."
                    rows="5"
                    required
                  ></textarea>
                </div>

                <button type="submit" className="form-submit-button">
                  <Send className="submit-icon" />
                  Send Message
                </button>
              </form>
            </div>
          </div>

          {/* Map and Additional Info */}
          <div className="contact-map-wrapper">
            {/* Map */}
            <div className="map-container">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3424.748133570187!2d75.91931459999999!3d30.865725899999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x209f1c579952010d%3A0x92d9d9a7d498666!2sEdsy%20Tech!5e0!3m2!1sen!2sin!4v1763016601034!5m2!1sen!2sin" width="600" height="450" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade" title='Map'></iframe>
            </div>

            {/* Quick Info */}
            <div className="quick-info-card">
              <h3 className="quick-info-title">Why Choose Us?</h3>
              <ul className="quick-info-list">
                <li className="quick-info-item">
                  <CheckCircle className="quick-info-icon" />
                  <span>10+ Years of Experience</span>
                </li>
                <li className="quick-info-item">
                  <CheckCircle className="quick-info-icon" />
                  <span>Expert Full Stack Team</span>
                </li>
                <li className="quick-info-item">
                  <CheckCircle className="quick-info-icon" />
                  <span>Microsoft Technology Specialists</span>
                </li>
                <li className="quick-info-item">
                  <CheckCircle className="quick-info-icon" />
                  <span>24/7 System & Business Support</span>
                </li>
                <li className="quick-info-item">
                  <CheckCircle className="quick-info-icon" />
                  <span>Proven Track Record</span>
                </li>
                <li className="quick-info-item">
                  <CheckCircle className="quick-info-icon" />
                  <span>Competitive Pricing</span>
                </li>
              </ul>
            </div>

            {/* Response Time */}
            <div className="response-time-card">
              <Clock className="response-time-icon" />
              <h4 className="response-time-title">Quick Response Time</h4>
              <p className="response-time-text">
                We typically respond to all inquiries within 24 hours during business days.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="contact-cta-banner">
          <div className="contact-cta-content">
            <h3 className="contact-cta-title">Need Immediate Assistance?</h3>
            <p className="contact-cta-text">
              Call us directly at <strong>+91 96423 00006</strong> or email <strong>info@edsytech.com</strong>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;